/**
 * Project-level angle types for managing source angles (house exterior photos)
 * that belong to a specific project.
 */

import type { UploadPolicy } from '@/types/project-reference';

export type ProjectAngleValidationStatus =
  | 'pending'
  | 'processing'
  | 'valid'
  | 'invalid'
  | 'prohibited'
  | 'technical_error';

/**
 * Flat validation meta from the API. Mirrors `ConceptAngleResource` so a single
 * parser covers both endpoints. The `status` flag is the source of truth —
 * frontends must not derive "invalid" from a missing row or from combining
 * multiple fields (API-driven state flags rule).
 */
export interface ProjectAngleValidation {
  status: ProjectAngleValidationStatus;
  is_valid: boolean | null;
  can_replace: boolean;
  is_technical_error?: boolean;
  recommendations?: string | null;
  form_of_source?: string | null;
  source_building_style?: string | null;
  building_type?: string | null;
  stage_of_construction?: string | null;
}

export interface ProjectAngleAttributes {
  file_id: string;
  name: string | null;
  description: string | null;
  original_filename: string | null;
  is_main: boolean;
  order: number;
  file_url: string | null;
  width: number | null;
  height: number | null;
  validation: ProjectAngleValidation;
  validation_performed_at: string | null;
  concepts_count: number;
  concept_numbers: Array<{ id: string; number: number }>;
  created_at: string;
  updated_at: string;
}

export interface ProjectAngle {
  id: string;
  type: 'angle';
  attributes: ProjectAngleAttributes;
}

export interface ProjectAngleUploadResponse {
  id: string;
  type: 'angle';
  attributes: {
    file: {
      md5: string;
      url: string | null;
    };
    // `upload_policy` is null for soft-deleted angles; callers must guard.
    upload_policy: UploadPolicy | null;
    original_filename: string | null;
    validation: ProjectAngleValidation;
  };
}

export function getProjectAngleValidationStatus(
  angle: ProjectAngle
): ProjectAngleValidationStatus {
  return angle.attributes.validation.status;
}

export function isProjectAngleValid(angle: ProjectAngle): boolean {
  return angle.attributes.validation.status === 'valid';
}

export function getProjectAngleDisplayName(
  angle: ProjectAngle,
  index: number
): string {
  return (
    angle.attributes.name ||
    angle.attributes.original_filename ||
    `Angle #${index + 1}`
  );
}
